import React, { useState, useEffect, useRef } from "react";
import type { ImageFile } from "../App";

interface EditableImageFile extends ImageFile {
  editedUrl?: string;
}

interface EditModalProps {
  image: EditableImageFile;
  isOpen: boolean;
  onClose: () => void;
  onSave: (editedImageUrl: string) => void;
  isDarkMode: boolean;
}

export function EditModal({ image, isOpen, onClose, onSave, isDarkMode }: EditModalProps) {
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [blur, setBlur] = useState(0);
  const [backgroundColor, setBackgroundColor] = useState('transparent');
  const [customColor, setCustomColor] = useState('#ffffff');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (isOpen) {
      setBrightness(100);
      setContrast(100);
      setSaturation(100);
      setBlur(0);
      setBackgroundColor('transparent');
      setCustomColor('#ffffff');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const processedURL = image.editedUrl || (image.processedFile ? URL.createObjectURL(image.processedFile) : "");

  
  const filterStyle = {
    filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px)`
  };

  const getBackgroundStyle = () => {
    if (backgroundColor === 'transparent') {
      return {
        background: isDarkMode 
          ? `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURTMzM////0EEUjQAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`
          : `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`,
        backgroundRepeat: 'repeat'
      };
    } else if (backgroundColor === 'custom') {
      return { backgroundColor: customColor };
    } else {
      return { backgroundColor };
    }
  };

  const applyFiltersToCanvas = async (): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const img = imageRef.current;
      
      if (!canvas || !img) {
        resolve(processedURL);
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(processedURL);
        return;
      }

      // Set canvas size to match image
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      // Apply background color if not transparent
      if (backgroundColor !== 'transparent') {
        const bgColor = backgroundColor === 'custom' ? customColor : backgroundColor;
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Apply filters to context
      ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px)`;
      
      // Draw the image
      ctx.drawImage(img, 0, 0);

      // Convert canvas to blob URL
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          resolve(url);
        } else {
          resolve(processedURL);
        }
      }, 'image/png');
    });
  };

  const handleSave = async () => {
    const editedImageUrl = await applyFiltersToCanvas();
    onSave(editedImageUrl);
    onClose();
  };

  const handleReset = () => {
    setBrightness(100);
    setContrast(100);
    setSaturation(100);
    setBlur(0);
    setBackgroundColor('transparent');
    setCustomColor('#ffffff');
  };

  const backgroundOptions = [
    { value: 'transparent', label: 'Transparan', color: null },
    { value: '#ffffff', label: 'Putih', color: '#ffffff' },
    { value: '#000000', label: 'Hitam', color: '#000000' },
    { value: '#ef4444', label: 'Merah', color: '#ef4444' },
    { value: '#3b82f6', label: 'Biru', color: '#3b82f6' },
    { value: '#22c55e', label: 'Hijau', color: '#22c55e' },
    { value: '#eab308', label: 'Kuning', color: '#eab308' },
    { value: '#8b5cf6', label: 'Ungu', color: '#8b5cf6' },
    { value: '#f97316', label: 'Oranye', color: '#f97316' },
    { value: 'custom', label: 'Kustom', color: null }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 modal-backdrop">
      <div className={`max-w-5xl w-full max-h-[95vh] overflow-y-auto rounded-lg shadow-xl transition-colors duration-300 scrollbar-thin ${
        isDarkMode ? 'bg-gray-800' : 'bg-white'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b transition-colors duration-300 ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl sm:text-2xl font-bold transition-colors duration-300 ${
            isDarkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Edit Gambar
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors duration-300 focus-ring ${
              isDarkMode 
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Image Preview */}
            <div>
              <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Priview
              </h3>
              <p>*Gambar yang sudah disimpan tidak dapat dikembalikan ke semula lagi</p>
              <div className="relative">
                <div 
                  className="w-full aspect-square rounded-lg overflow-hidden card-shadow"
                  style={getBackgroundStyle()}
                >
                  {processedURL && (
                    <>
                      <img
                        ref={imageRef}
                        src={processedURL}
                        alt="Pratinjau edit"
                        className="w-full h-full object-cover"
                        style={filterStyle}
                        crossOrigin="anonymous"
                      />
                      {/* Hidden canvas for processing */}
                      <canvas
                        ref={canvasRef}
                        className="hidden"
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Controls */}
            <div>
              <h3 className={`text-lg font-semibold mb-3 transition-colors duration-300 ${
                isDarkMode ? 'text-gray-200' : 'text-gray-700'
              }`}>
                Pengaturan
              </h3>
              <div className="space-y-6">
                {/* Background Color Section */}
                <div>
                  <label className={`block text-sm font-medium mb-3 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    Warna Latar Belakang
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
                    {backgroundOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setBackgroundColor(option.value)}
                        className={`relative h-12 rounded-lg border-2 transition-all duration-200 hover-scale ${
                          backgroundColor === option.value
                            ? 'border-blue-500 ring-2 ring-blue-200'
                            : isDarkMode
                            ? 'border-gray-600 hover:border-gray-500'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        style={{
                          backgroundColor: option.color || 'transparent',
                          backgroundImage: option.value === 'transparent' 
                            ? isDarkMode
                              ? `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURTMzM////0EEUjQAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`
                              : `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`
                            : 'none',
                          backgroundRepeat: 'repeat'
                        }}
                        title={option.label}
                      >
                        {/* Checkmark for selected option */}
                        {backgroundColor === option.value && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-6 h-6 text-blue-600 drop-shadow-lg" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        {/* Custom color icon */}
                        {option.value === 'custom' && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM7 3V1m0 18v2m8-10h2m-2 0h2m-2 0v2m0-2v-2" />
                            </svg>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                  
                  {/* Custom Color Picker */}
                  {backgroundColor === 'custom' && (
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={customColor}
                        onChange={(e) => setCustomColor(e.target.value)}
                        className="w-12 h-8 rounded border-2 border-gray-300 cursor-pointer"
                      />
                      <span className={`text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        Pilih warna kustom: {customColor}
                      </span>
                    </div>
                  )}
                </div>

                {/* Filter Controls */}
                <div className="space-y-4">
                  {/* Brightness */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      Kecerahan: {brightness}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                      className={`w-full h-2 rounded-lg appearance-none cursor-pointer transition-colors duration-300 focus-ring ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}
                    />
                  </div>

                  {/* Contrast */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      Kontras: {contrast}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={contrast}
                      onChange={(e) => setContrast(Number(e.target.value))}
                      className={`w-full h-2 rounded-lg appearance-none cursor-pointer transition-colors duration-300 focus-ring ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}
                    />
                  </div>

                  {/* Saturation */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      Saturasi: {saturation}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="200"
                      value={saturation}
                      onChange={(e) => setSaturation(Number(e.target.value))}
                      className={`w-full h-2 rounded-lg appearance-none cursor-pointer transition-colors duration-300 focus-ring ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}
                    />
                  </div>

                  {/* Blur */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      Blur: {blur}px
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={blur}
                      onChange={(e) => setBlur(Number(e.target.value))}
                      className={`w-full h-2 rounded-lg appearance-none cursor-pointer transition-colors duration-300 focus-ring ${
                        isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                      }`}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleReset}
                    className={`flex-1 px-4 py-2 rounded-md border transition-all duration-300 focus-ring hover-scale ${
                      isDarkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Reset
                  </button>
                  <button
                    onClick={onClose}
                    className={`flex-1 px-4 py-2 rounded-md border transition-all duration-300 focus-ring hover-scale ${
                      isDarkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                    }`}
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Batal
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-all duration-300 focus-ring hover-scale shadow-lg"
                  >
                    <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Simpan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}